import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Loader2,
  Upload,
  Link as LinkIcon,
  FileText,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT =
  ".pdf,.dwg,.dxf,.skp,.rvt,.ifc,.png,.jpg,.jpeg,.webp,.heic,.zip,.rar,.7z,.docx,.xlsx,.pptx";
const MAX_BYTES = 25 * 1024 * 1024;

export default function QuoteDialog({ open, onClose }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "Residential",
    message: "",
    plans_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null); // { file_url, file_name, size }
  const [dragActive, setDragActive] = useState(false);

  if (!open) return null;

  const handleChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const formatSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large. Max 25 MB.");
      return;
    }
    try {
      setUploading(true);
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/upload`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(res.data);
      toast.success(`Uploaded ${res.data.file_name}`);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Upload failed. Try a different file.";
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
    e.target.value = ""; // allow re-upload of same file later
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const removeUpload = () => setUploaded(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        plans_url: form.plans_url.trim() || null,
        plans_file_url: uploaded?.file_url || null,
        plans_file_name: uploaded?.file_name || null,
      };
      await axios.post(`${API}/quote`, payload);
      toast.success("Thanks! Your quote request has been received.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: "Residential",
        message: "",
        plans_url: "",
      });
      setUploaded(null);
      onClose?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Invalid submission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="quote-dialog"
      className="fixed inset-0 z-[2000] flex items-end md:items-center justify-center p-0 md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-up"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl bg-white shadow-2xl rounded-none md:rounded-sm overflow-hidden animate-fade-up">
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-neutral-200">
          <div>
            <span className="eyebrow">Begin the Conversation</span>
            <h3 className="heading-lg mt-3 text-[26px] md:text-[32px] text-[#0f2420]">
              Request a Consultation
            </h3>
            <p className="text-neutral-600 text-[13.5px] mt-2 font-nunito">
              Tell us about your project — we'll respond within one business
              day.
            </p>
          </div>
          <button
            data-testid="quote-dialog-close"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full border border-neutral-200 text-neutral-600 hover:bg-neutral-100 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="p-6 md:p-8 space-y-4 max-h-[70vh] overflow-auto"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Full Name"
              required
              value={form.name}
              onChange={handleChange("name")}
              testId="quote-field-name"
            />
            <Field
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={handleChange("email")}
              testId="quote-field-email"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={handleChange("phone")}
              testId="quote-field-phone"
            />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.24em] font-montserrat text-neutral-600 mb-2">
                Project Type
              </label>
              <select
                data-testid="quote-field-project-type"
                value={form.project_type}
                onChange={handleChange("project_type")}
                className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm font-nunito outline-none focus:border-[#c99658] transition-colors"
              >
                <option>Residential</option>
                <option>Commercial</option>
                <option>Architectural / Custom</option>
                <option>Multi-family / Development</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.24em] font-montserrat text-neutral-600 mb-2">
              Tell us about your project
            </label>
            <textarea
              data-testid="quote-field-message"
              rows={4}
              value={form.message}
              onChange={handleChange("message")}
              placeholder="Dimensions, timeline, product interest..."
              className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm font-nunito outline-none focus:border-[#c99658] transition-colors resize-none"
            />
          </div>

          {/* Architectural plans section */}
          <div className="pt-2 border-t border-neutral-200/80">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-[11px] uppercase tracking-[0.32em] font-raleway text-[#0f2420] font-semibold">
                Architectural Plans
              </label>
              <span className="text-[10px] tracking-[0.24em] uppercase text-neutral-400 font-raleway">
                Optional
              </span>
            </div>

            {/* Upload zone or selected file */}
            {uploaded ? (
              <div
                data-testid="quote-uploaded-file"
                className="flex items-center gap-3 p-3 border border-[#c99658]/40 bg-cream"
              >
                <FileText className="w-5 h-5 text-[#7a5b2f] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#0f2420] font-medium truncate">
                    {uploaded.file_name}
                  </div>
                  <div className="text-[11px] text-neutral-600 font-nunito">
                    {formatSize(uploaded.size)} · uploaded
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="quote-remove-file"
                  onClick={removeUpload}
                  className="text-neutral-400 hover:text-red-500 transition-colors"
                  aria-label="Remove file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                className={`flex flex-col items-center justify-center gap-2 p-5 border border-dashed cursor-pointer transition-colors ${
                  dragActive
                    ? "border-[#c99658] bg-cream"
                    : "border-neutral-300 hover:border-[#c99658] hover:bg-[#fbf8f0]"
                }`}
              >
                <input
                  ref={fileInputRef}
                  data-testid="quote-file-input"
                  type="file"
                  className="hidden"
                  accept={ACCEPT}
                  onChange={onFilePick}
                  disabled={uploading}
                />
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 text-[#c99658] animate-spin" />
                    <span className="text-[13px] text-neutral-600 font-nunito">
                      Uploading...
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-[#7a5b2f]" />
                    <span className="text-[13px] text-[#0f2420] font-medium">
                      Upload Architectural Plans
                    </span>
                    <span className="text-[11px] text-neutral-600 font-nunito text-center">
                      PDF, DWG, DXF, SKP, RVT, IFC, images, ZIP — up to 25 MB
                    </span>
                  </>
                )}
              </label>
            )}

            {/* Or link */}
            <div className="mt-4">
              <label className="block text-[11px] uppercase tracking-[0.24em] font-montserrat text-neutral-600 mb-2">
                Or paste a link to plans
              </label>
              <div className="flex items-center gap-2 border border-neutral-300 bg-white px-3 focus-within:border-[#c99658] transition-colors">
                <LinkIcon className="w-4 h-4 text-neutral-400 shrink-0" />
                <input
                  data-testid="quote-field-plans-url"
                  type="url"
                  value={form.plans_url}
                  onChange={handleChange("plans_url")}
                  placeholder="Dropbox, Google Drive, OneDrive, etc."
                  className="flex-1 py-3 text-sm font-nunito outline-none bg-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-[11px] text-neutral-600 font-nunito">
              By submitting you agree to be contacted about your request.
            </p>
            <button
              data-testid="quote-submit-btn"
              type="submit"
              disabled={submitting || uploading}
              className="btn-pill btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.24em] font-montserrat text-neutral-600 mb-2">
        {label}
        {required && <span className="text-[#c99658] ml-1">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm font-nunito outline-none focus:border-[#c99658] transition-colors"
      />
    </div>
  );
}
